import React from "react";
import { RfModal } from "./RfModal";
import { twClassNames } from "../../helpers/twClassNames";

type SubmitButtonProps = {
    type?: string;
    loading?: boolean;
    loadingText?: string;
    disabled?: boolean;
};

export type ModalWithTitleProps = {
    isOpen: boolean;
    onClose: () => void;
    onSubmit?: () => void;
    onCancel?: () => void;
    type?: "info" | "danger" | "warning" | "success";
    size?: "sm" | "md" | "lg" | "xl" | "xxl" | "xxxl" | "max";
    icon?: string;
    title: string | React.ReactNode;
    children?: React.ReactNode;
    className?: string;
    modalClassName?: string;
    positionClassName?: string;
    panelClassName?: string;
    headerClassName?: string;
    overlayClassName?: string;
    submitButtonText?: string | React.ReactNode;
    cancelButtonText?: string;
    showFooter?: boolean;
    footerComponent?: React.ReactNode | React.ComponentType;
    submitButtonProps?: SubmitButtonProps & React.ButtonHTMLAttributes<HTMLButtonElement>;
    footerCustomLeftSide?: React.ReactNode;
    scrollInsideModal?: boolean;
    disableSubmitOnEnter?: boolean;
    escapeToClose?: boolean;
    onCloseButtonClicked?: () => void;
    closable?: boolean;
    allowClickPropagation?: boolean;
    allowMouseUpPropagation?: boolean;
};

const styles = {
    sm: "max-w-lg", // same size as swal
    md: "max-w-xl",
    lg: "max-w-3xl",
    xl: "max-w-4xl",
    xxl: "max-w-5xl",
    xxxl: "max-w-6xl",
    max: "max-w-none"
};

export function ModalWithTitle({
    isOpen,
    onClose,
    type = "info",
    size = "md",
    icon = "",
    title,
    children,
    className,
    modalClassName,
    positionClassName,
    panelClassName,
    headerClassName,
    overlayClassName,
    onSubmit,
    submitButtonText,
    onCancel,
    cancelButtonText,
    showFooter = true,
    footerComponent,
    submitButtonProps,
    footerCustomLeftSide,
    scrollInsideModal,
    disableSubmitOnEnter,
    escapeToClose = true,
    onCloseButtonClicked,
    closable = true,
    allowClickPropagation,
    allowMouseUpPropagation
}: ModalWithTitleProps) {
    const maxSize = styles[size];

    return (
        <RfModal
            isOpen={isOpen}
            onClose={onClose}
            className={twClassNames(modalClassName, scrollInsideModal && "h-screen", "modal")}
            disableSubmitOnEnter={disableSubmitOnEnter || !closable}
            escapeToClose={escapeToClose && !!closable}
        >
            <RfModal.Overlay className={overlayClassName} />
            <RfModal.Panel
                className={twClassNames(`${maxSize} p-0`, panelClassName)}
                positionClassName={twClassNames(
                    positionClassName,
                    scrollInsideModal && "h-fit flex-col justify-start"
                )}
                allowClickPropagation={allowClickPropagation}
                allowMouseUpPropagation={allowMouseUpPropagation}
            >
                <RfModal.Header
                    className={twClassNames(
                        "flex items-center justify-between gap-3",
                        headerClassName,
                        "border-gray-200"
                    )}
                >
                    <RfModal.Title className="flex items-center gap-3">
                        {icon && <i className={twClassNames(icon, "text-gray-500")}></i>}
                        <span className="flex-1">{title}</span>
                    </RfModal.Title>
                    {closable && <RfModal.CloseButton onClickOverride={onCloseButtonClicked} />}
                </RfModal.Header>

                <div
                    className={twClassNames(
                        "flex flex-col gap-6 p-6 text-base text-secondary",
                        className,
                        scrollInsideModal && "h-full overflow-auto"
                    )}
                >
                    {children}
                </div>

                {showFooter && (
                    <>
                        {footerComponent ? (
                            <div
                                className={twClassNames("border-t-1 px-6 py-4", "border-gray-200")}
                            >
                                {footerComponent}
                            </div>
                        ) : (
                            <RfModal.Footer
                                onSubmit={onSubmit}
                                onCancel={onCancel}
                                submitButtonText={submitButtonText}
                                cancelButtonText={cancelButtonText}
                                submitButtonType={type === "danger" ? "red" : "primary"}
                                submitButtonProps={submitButtonProps}
                                isFullWidth={false}
                                hasTopBorder={true}
                                footerCustomLeftSide={footerCustomLeftSide}
                                className="border-gray-200"
                            />
                        )}
                    </>
                )}
            </RfModal.Panel>
        </RfModal>
    );
}
